import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    showOrgSurveyMessage,
    hideOrgSurveyLoader,
    fetchOrgSurveysCompareSuccess,
    fetchOrgSurveysCompareRadarChartDataSuccess,
    fetchOrgSurveysCompareDistributionsChartDataSuccess,
    fetchOrgSurveysCompareTopResultsChartDataSuccess,
    fetchOrgSurveysCompareBottomResultsChartDataSuccess,
    fetchOrgSurveysEngagementReportChartDataSuccess,
} from 'actions/OrgSurvey';
import { onSaveOrgSurveyAnswers } from 'actions/OrgSurveyAnswers';
import { fetchSurveysSuccess } from 'actions/Survey';
import { onSaveOrg } from 'actions/Organisation';
import {
    ON_SAVE_ORG_SURVEY,
    FETCH_ALL_ORG_SURVEY,
    FETCH_ALL_ORG_SURVEY_COMPARE,
    FETCH_ALL_ORG_SURVEY_COMPARE_RADAR_CHART_DATA,
    FETCH_ALL_ORG_SURVEY_COMPARE_DISTRIBUTIONS_CHART_DATA,
    FETCH_ALL_ORG_SURVEY_COMPARE_TOP_RESULTS_CHART_DATA,
    FETCH_ALL_ORG_SURVEY_COMPARE_BOTTOM_RESULTS_CHART_DATA,
    FETCH_ALL_ORG_SURVEY_ENGAGEMENT_REPORT_CHART_DATA
} from 'constants/ActionTypes';
import { fetchDataRequest } from 'services/ApiService';
import { generateGuid } from 'services/Authentication';
import {
    createDefaultOrgSurveyAnswers,
    getInactiveSurveys,
    initialiseOrgSurveysCompareList,
    extractOrgSurveyRadarData,
    extractOrgSurveyDistributionsData,
    extractOrgSurveyTopResponseData,
    extractOrgSurveyBottomResponseData,
    prepareOrgSurveyAnswerSagaCall,
    getCompareOrgSurveyAnswers
} from 'services/SurveyService';


/**
 * An organisation should have been selected so just need to retrieve the survey list here
 */
function* fetchOrgSurveys() {
    try {
        const fetchedSurveys = yield call(fetchDataRequest, 'surveys') || [];
        yield put(fetchSurveysSuccess(fetchedSurveys));
        yield put(hideOrgSurveyLoader());
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

const getSelectedOrg = (state) => state.orgs.selectedOrg;

/**
 * For the selected org - get all the inactive/ finished surveys (i.e. past the end date)
 */
function* fetchOrgSurveysCompare() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const inactiveSurveys = getInactiveSurveys(selectedOrg?.surveys);
        yield put(fetchOrgSurveysCompareSuccess(initialiseOrgSurveysCompareList(inactiveSurveys)));
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

const getSelectedOrgSurveyCompareList = (state) => state.orgSurveys.orgSurveyCompareList;

/**
 * 
 * From the selected surveys used to compare results - build the chart data required for the visualisations
 * TODO - rename to fetchOrgSurveysRadarChartData  - along with actions etc
 */
function* fetchOrgSurveysCompareRadarChartData() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const orgSurveysCompareList = yield select(getSelectedOrgSurveyCompareList);
        const orgSurveyAnswerSagaCall = yield call(prepareOrgSurveyAnswerSagaCall, orgSurveysCompareList, selectedOrg?.id);
        const orgSurveyAnswers = orgSurveyAnswerSagaCall ? yield orgSurveyAnswerSagaCall : [];
        // extract the different types of chart data and thne update the store
        const orgSurveysCompareRadarChartData = extractOrgSurveyRadarData(orgSurveyAnswers); // surveyService call
        yield put(fetchOrgSurveysCompareRadarChartDataSuccess(orgSurveysCompareRadarChartData));
        // }
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

/**
 * 
 * From the selected surveys used to compare results - build the chart data required for the visualisations
 * 
 */
function* fetchOrgSurveysEngagementReportData() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const orgSurveysCompareList = yield select(getSelectedOrgSurveyCompareList);
        const orgSurveyAnswers = yield call(getCompareOrgSurveyAnswers, orgSurveysCompareList, selectedOrg?.id);
        // extract the different types of chart data and then update the store
        const orgSurveysCompareDistributionsChartData = extractOrgSurveyDistributionsData(orgSurveyAnswers); // surveyService call
        const orgSurveysCompareTopResultsChartData = orgSurveyAnswers.map((answer) => {
            return extractOrgSurveyTopResponseData(orgSurveyAnswers, answer?.id);
        });
        const orgSurveysCompareBottomResultsChartData = orgSurveyAnswers.map((answer) => {
            return extractOrgSurveyBottomResponseData(orgSurveyAnswers, answer?.id);
        });
        const engagementReportData = {
            topResultsChartData: orgSurveysCompareTopResultsChartData,
            bottomResultsChartData: orgSurveysCompareBottomResultsChartData,
            distributionsReportData: orgSurveysCompareDistributionsChartData
        };
        yield put(fetchOrgSurveysEngagementReportChartDataSuccess(engagementReportData));
        // }
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}


/**
 * 
 * From the selected surveys used to compare results - build the chart data required for the visualisations
 * 
 */
function* fetchOrgSurveysDistributionsChartData() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const orgSurveysCompareList = yield select(getSelectedOrgSurveyCompareList);
        const orgSurveyAnswers = yield call(getCompareOrgSurveyAnswers, orgSurveysCompareList, selectedOrg?.id);
        // extract the different types of chart data and then update the store
        const orgSurveysCompareDistributionsChartData = extractOrgSurveyDistributionsData(orgSurveyAnswers); // surveyService call
        yield put(fetchOrgSurveysCompareDistributionsChartDataSuccess(orgSurveysCompareDistributionsChartData));
        // }
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}


/**
 * 
 * From the selected surveys used to compare results - build the chart data required for the visualisations
 * 
 */
function* fetchOrgSurveysTopResultsChartData() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const orgSurveysCompareList = yield select(getSelectedOrgSurveyCompareList);
        const orgSurveyAnswers = yield call(getCompareOrgSurveyAnswers, orgSurveysCompareList, selectedOrg?.id);
        const orgSurveysCompareTopResultsChartData = orgSurveyAnswers.map((answer) => {
            return extractOrgSurveyTopResponseData(orgSurveyAnswers, answer?.id);
        });
        yield put(fetchOrgSurveysCompareTopResultsChartDataSuccess(orgSurveysCompareTopResultsChartData));
        // }
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

/**
 * 
 * From the selected surveys used to compare results - build the chart data required for the visualisations
 * 
 */
function* fetchOrgSurveysBottomResultsChartData() {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const orgSurveysCompareList = yield select(getSelectedOrgSurveyCompareList);
        const orgSurveyAnswers = yield call(getCompareOrgSurveyAnswers, orgSurveysCompareList, selectedOrg?.id);
        const orgSurveysCompareBottomResultsChartData = orgSurveyAnswers.map((answer) => {
            return extractOrgSurveyBottomResponseData(orgSurveyAnswers, answer?.id);
        });
        yield put(fetchOrgSurveysCompareBottomResultsChartDataSuccess(orgSurveysCompareBottomResultsChartData));
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

function* saveOrgSurvey({ payload }) {
    try {
        const selectedOrg = yield select(getSelectedOrg);
        const surveyId = payload?.surveyId;
        const generatedId = generateGuid();
        const orgSurveyPayload = yield call(orgSurveyPayloadRequest, selectedOrg, payload, generatedId);
        yield put(onSaveOrg(orgSurveyPayload));
        // also create the org survey answers entry - questionAnswers are empty until employees access the survey
        yield put(onSaveOrgSurveyAnswers(createDefaultOrgSurveyAnswers(generatedId, selectedOrg.id, surveyId)));
        yield put(hideOrgSurveyLoader());
    } catch (error) {
        yield put(showOrgSurveyMessage(error.messages));
    }
}

/**
 * builds the org survey payload adding the new payload to the list
 * @param {*} selectedOrg 
 * @param {*} payload - will add the survey or replace if it exists in the org survey list
 * @param {*} id - need to pass in as we are using the same id for the corresponding org survey answers data entry 
 */
const orgSurveyPayloadRequest = async (selectedOrg, payload, id) => {
    // simply add to the selected org survey list and then call save the organisation.
    try {
        if (selectedOrg?.surveys) {
            // check if the survey already exists
            let isNew = true;
            const surveyList = selectedOrg.surveys.map((survey) => {
                if (survey.id === payload.id) {
                    isNew = false;
                    return payload;
                } else {
                    return survey;
                }
            });
            if (isNew) {
                // payload not injected into the list so add an id and push
                const newPayload = { ...payload, id };
                surveyList.push(newPayload);
            }
            selectedOrg.surveys = surveyList;
        } else {
            // no surveys created so add the first
            const surveys = [];
            const newPayload = { ...payload, id };
            surveys.push(newPayload);
            selectedOrg.surveys = surveys;
        }
        return selectedOrg;
    } catch (error) {
        throw error;
    }
};

export function* fetchOrgSurveysSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY, fetchOrgSurveys);
}

export function* fetchOrgSurveysCompareSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_COMPARE, fetchOrgSurveysCompare);
}

export function* fetchOrgSurveysCompareRadarChartDataSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_COMPARE_RADAR_CHART_DATA, fetchOrgSurveysCompareRadarChartData);
}

export function* fetchOrgSurveysDistributionsChartDataSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_COMPARE_DISTRIBUTIONS_CHART_DATA, fetchOrgSurveysDistributionsChartData);
}

export function* fetchOrgSurveysTopResultsChartDataSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_COMPARE_TOP_RESULTS_CHART_DATA, fetchOrgSurveysTopResultsChartData);
}

export function* fetchOrgSurveysBottomResultsChartDataSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_COMPARE_BOTTOM_RESULTS_CHART_DATA, fetchOrgSurveysBottomResultsChartData);
}

export function* fetchOrgSurveysEngagementReportDataSaga() {
    yield takeEvery(FETCH_ALL_ORG_SURVEY_ENGAGEMENT_REPORT_CHART_DATA, fetchOrgSurveysEngagementReportData);
}

export function* saveOrgSurveySaga() {
    yield takeEvery(ON_SAVE_ORG_SURVEY, saveOrgSurvey);
}

export default function* rootSaga() {
    yield all([
        fork(fetchOrgSurveysSaga),
        fork(saveOrgSurveySaga),
        fork(fetchOrgSurveysCompareSaga),
        fork(fetchOrgSurveysCompareRadarChartDataSaga),
        fork(fetchOrgSurveysDistributionsChartDataSaga),
        fork(fetchOrgSurveysTopResultsChartDataSaga),
        fork(fetchOrgSurveysBottomResultsChartDataSaga),
        fork(fetchOrgSurveysEngagementReportDataSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/OrgSurvey.js